/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { U2000ExtractFirstApiError } from "../u2000Core/axios/U2000Axios";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { U2000LUStateEnumNextAction } from "../u2000Core/reducer/formReducer/U2000LUState";
import U3125FactureAddDlg from "./components/common/U3125FactureAddDlg";
import { NS } from "./U3125";
import { luHandleAppiError, type U3125InitCreateDocumentNature, type U3125InitCreateNewInvoice } from "./U3125Service";
import { useU3125InitCreateHandlers } from "./U3125UseApiHandlers";
import { useU3125Context } from "./U3125Context";

export type U3125FormNavigationState = {
  initPreview?: U3125InitCreateNewInvoice;
};

interface U3125InitCreateInvoiceDialogContextValue {
  openAddDialog: () => void;
}

const U3125InitCreateInvoiceDialogContext = createContext<U3125InitCreateInvoiceDialogContextValue | undefined>(undefined);

function readErrorParameter(
  parameters: Array<{ name?: string; value?: string }> | undefined,
  name: string,
): string | undefined {
  return parameters?.find((p) => p.name === name)?.value?.trim() || undefined;
}

export const U3125InitCreateInvoiceDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const { luStateHandlers } = useU3125Context();

  const handleInitCreateError = useCallback(
    (error: unknown) => {
      const payload = U2000ExtractFirstApiError(error);
      if (payload?.errorCode === "BU312530") {
        const existingInvoiceId = readErrorParameter(payload.parameters, "existingInvoiceId");
        if (existingInvoiceId) {
          setOpen(false);
          toast.info(t("errBU312530Action", { ns: NS }), t("lblWarningsTitle", { ns: NS }));
          navigate(`/U3125/${existingInvoiceId}`);
          return;
        }
      }

      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [navigate, t, toast],
  );

  const { initCreateNewInvoice } = useU3125InitCreateHandlers({ onError: handleInitCreateError });

  const openAddDialog = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleConfirm = useCallback(
    async (projetNumber: string, documentNature: U3125InitCreateDocumentNature) => {
      const preview = await initCreateNewInvoice({ projetNumber: projetNumber.trim(), documentNature });
      if (!preview?.project?.id) {
        if (preview !== null) {
          toast.error(t("errInitCreateInvalidResponse", { ns: NS }), t("errorTitle", { ns: NS }));
        }
        return;
      }
      setOpen(false);
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3125");
      navigate("/U3125/new", { state: { initPreview: preview } satisfies U3125FormNavigationState });
    },
    [initCreateNewInvoice, luStateHandlers, navigate, t, toast],
  );

  const value = useMemo(() => ({ openAddDialog }), [openAddDialog]);

  return (
    <U3125InitCreateInvoiceDialogContext.Provider value={value}>
      {children}
      <U3125FactureAddDlg isOpen={open} onClose={handleClose} onConfirm={handleConfirm} />
    </U3125InitCreateInvoiceDialogContext.Provider>
  );
};

export function useU3125InitCreateInvoiceDialog(): U3125InitCreateInvoiceDialogContextValue {
  const ctx = useContext(U3125InitCreateInvoiceDialogContext);
  if (!ctx) {
    throw new Error("useU3125InitCreateInvoiceDialog must be used within U3125InitCreateInvoiceDialogProvider");
  }
  return ctx;
}
